import type { ArgsOf, RequestOf } from "@p0security/shared/types/access/index";
import { Access } from "@p0security/shared/types/access/index";

export const zpaResourceParameters = {
  tenant: {
    type: "static" as const,
    label: "ZPA tenant",
    description:
      "The Zscaler Private Access tenant (identified by vanity domain)",
    optional: true as const,
    isPreselect: true,
    waitForInput: true,
  },
  applicationSegment: {
    type: "static" as const,
    label: "Application segment",
    description: "The name or ID of the application segment to grant access to",
  },
  applicationSegmentName: {
    type: "generated" as const,
    label: "Application segment name",
    description: "The name of the application segment",
  },
  applicationSegmentId: {
    type: "generated" as const,
    label: "Application segment ID",
    description: "The ID of the application segment",
  },
  segmentGroupId: {
    type: "generated" as const,
    label: "Segment group ID",
    description: "The ID of the segment group",
  },
  segmentGroupName: {
    type: "generated" as const,
    label: "Segment group name",
    description: "The name of the segment group",
  },
  policySetId: {
    type: "generated" as const,
    label: "Policy set ID",
    description: "The ID of the ACCESS_POLICY policy set",
  },
  userIdpId: {
    type: "generated" as const,
    label: "User IDP ID",
    description: "The ID of the USER identity provider",
  },
  primaryEmailAttributeId: {
    type: "generated" as const,
    label: "Primary email attribute ID",
    description: "The ID of the primaryemail SCIM attribute",
  },
  isZscalerDelegated: {
    type: "generated" as const,
    label: "Requires Zscaler access provisioning?",
    description: "Flag whether access provisioning happens in Zscaler",
  },
} as const;

/**
 * Subset of {@link zpaResourceParameters} that only includes
 * segment identification fields. Used by the SSH integration to store network
 * access information without requiring iam-write permissions. Policy-management
 * fields (policySetId, userIdpId, primaryEmailAttributeId) are resolved at
 * grant time via the iam-write normalize step.
 */
export const zpaNetworkAccessParameters = {
  tenant: zpaResourceParameters.tenant,
  applicationSegment: zpaResourceParameters.applicationSegment,
  applicationSegmentName: zpaResourceParameters.applicationSegmentName,
  applicationSegmentId: zpaResourceParameters.applicationSegmentId,
  segmentGroupId: zpaResourceParameters.segmentGroupId,
  segmentGroupName: zpaResourceParameters.segmentGroupName,
  isZscalerDelegated: zpaResourceParameters.isZscalerDelegated,
} as const;

export const ZpaAccesses = {
  "application-segment": Access({
    delegation: {},
    description:
      "Add an access rule for an application segment in Zscaler Private Access",
    label: "Application segment",
    maturity: "preview",
    generated: {
      ruleName: {
        type: "string" as const,
      },
    },
    parameters: zpaResourceParameters,
    render: { locator: "applicationSegmentId" } as const,
  }),
};

export type ZpaAccesses = typeof ZpaAccesses;
export type ZpaAccess = ZpaAccesses[keyof ZpaAccesses];

export type ZpaApplicationSegmentAccess = ZpaAccesses["application-segment"];

export type ZpaApplicationSegmentRequest =
  RequestOf<ZpaApplicationSegmentAccess>;

export type ZpaApplicationSegmentAccessArgs =
  ArgsOf<ZpaApplicationSegmentAccess>;

export type ZpaNetwork = Pick<
  ZpaApplicationSegmentRequest["permission"],
  | "applicationSegment"
  | "applicationSegmentId"
  | "applicationSegmentName"
  | "isZscalerDelegated"
  | "segmentGroupId"
  | "segmentGroupName"
  | "tenant"
>;
